import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {
  BeachAccess,
  CalendarMonth,
  CheckCircle,
  Close,
  Download,
  EventNote,
  FilterList,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  MedicalServices,
  PregnantWoman,
  Search,
  Visibility
} from "@mui/icons-material";
import Page from "src/components/Page";
import { notification } from "src/utils/messages";
import CustomLoader from "src/commonComponents/CustomLoader";
import { getLeaves, getLeaveDetail, updateLeaveStatus } from "src/api/leaveRequests";
import { getLeaveTypes } from "src/api/leaveTypes";
import moment from "moment";

const LVB = "#0B374D";
const LVB_HOVER = "#0E4560";

const statusStyle = {
  0: { label: "Pending", bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  1: { label: "Rejected", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  2: { label: "Approved", bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
  3: { label: "Withdrawn", bg: "#e2e8f0", color: "#334155", dot: "#64748b" },
  4: { label: "Cancelled", bg: "#e2e8f0", color: "#334155", dot: "#64748b" }
};

const leaveIcon = (name) => {
  const t = (name || "").toLowerCase();
  if (t.includes("medic")) return MedicalServices;
  if (t.includes("matern")) return PregnantWoman;
  if (t.includes("travel") || t.includes("holiday") || t.includes("vacation")) return BeachAccess;
  if (t.includes("emerg")) return EventNote;
  return CalendarMonth;
};

const leaveColor = (id) => {
  const colors = [
    ["#e0e7ff", "#3730a3"],
    ["#dbeafe", "#1e40af"],
    ["#dcfce7", "#166534"],
    ["#fef9c3", "#854d0e"],
    ["#fce7f3", "#9d174d"]
  ];
  return colors[(id || 0) % colors.length];
};

const initials = (name) => {
  const parts = (name || "?").trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0] || "?").slice(0, 2).toUpperCase();
};

const avatarColor = (name) => {
  const palette = ["#0B374D", "#0284C7", "#0f766e", "#7c3aed", "#b45309", "#be123c"];
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

export default function LeaveRequests() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filter, setFilter] = useState("pending");
  const [onLeaveOnly, setOnLeaveOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [actModal, setActModal] = useState(false);
  const [actType, setActType] = useState(2);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [search, setSearch] = useState("");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState(0);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    getLeaveTypes().then((r) => {
      if (r?.status === 1) setLeaveTypes(r.output || []);
    });
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 440,
    bgcolor: "#fff",
    boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
    borderRadius: "14px",
    p: 3,
    maxHeight: "85vh",
    overflowY: "auto"
  };

  const load = async (f = filter, s = search, lt = leaveTypeFilter) => {
    setIsLoading(true);
    try {
      const res = await getLeaves({ chapter_id: 0, filter: f, search: s, leave_type: lt, page: 1, limit: 100 });
      if (res?.status === 1) {
        setRows(res.output?.data || []);
        setCounters(res.output?.counters || {});
      }
    } catch (e) {
      notification({ type: "error", message: "Failed to load leaves" })();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, leaveTypeFilter]);

  const setFilterTab = (f) => {
    if (f === "onleave") {
      setFilter("approved");
      setOnLeaveOnly(true);
    } else {
      setFilter(f);
      setOnLeaveOnly(false);
    }
  };

  const displayedRows = onLeaveOnly
    ? (rows || []).filter((r) => {
        const today = moment().format("DD-MM-YYYY");
        return (
          (r.status === 2 || r.status === "2") &&
          moment(r.start_date, "DD-MM-YYYY").isSameOrBefore(moment(today, "DD-MM-YYYY"), "day") &&
          moment(r.end_date, "DD-MM-YYYY").isSameOrAfter(moment(today, "DD-MM-YYYY"), "day")
        );
      })
    : rows;

  const openAct = (row, type) => {
    setSelected(row);
    setActType(type);
    setRemarks("");
    setActModal(true);
  };

  const openView = async (row) => {
    setViewModal(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await getLeaveDetail({ leave_id: row.leave_id });
      if (res?.status === 1) setDetail(res.output || row);
      else setDetail(row);
    } catch (e) {
      setDetail(row);
    }
    setDetailLoading(false);
  };

  const submitAct = async () => {
    if (!remarks.trim()) {
      notification({ type: "error", message: "Remarks required" })();
      return;
    }
    setIsLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await updateLeaveStatus({
        leave_id: selected.leave_id,
        status: actType,
        remarks,
        action_member_id: user?.u_id,
        action_member_role: 0,
        action_member_chapter_id: 0
      });
      notification({ type: res?.status === 1 ? "success" : "error", message: res?.message || "Done" })();
      if (res?.status === 1) {
        setActModal(false);
        load();
      }
    } catch (e) {
      notification({ type: "error", message: "Action failed" })();
    }
    setIsLoading(false);
  };

  const exportCsv = () => {
    const headers = ["Member", "Leave Type", "From", "To", "Days", "Status"];
    const lines = [headers.join(",")].concat(
      (displayedRows || []).map((r) =>
        [r.member_name, r.leave_type_name, r.start_date, r.end_date, r.total_days, statusStyle[r.status]?.label || ""]
          .map((v) => `"${String(v || "").replace(/"/g, '""')}"`)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leave-requests.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    { key: "all", label: "Total", value: counters.total_applied, icon: EventNote, color: "#0B374D", bg: "#e2ecf2", sub: "All requests", highlight: false },
    { key: "pending", label: "Pending", value: counters.pending, icon: CalendarMonth, color: "#92400e", bg: "#fef3c7", sub: "Needs Action", highlight: true },
    { key: "approved", label: "Approved", value: counters.approved, icon: CheckCircle, color: "#065f46", bg: "#d1fae5", sub: "Approved", highlight: false },
    { key: "rejected", label: "Rejected", value: counters.rejected, icon: Close, color: "#991b1b", bg: "#fee2e2", sub: "Policy limits", highlight: false },
    { key: "onleave", label: "On Leave", value: counters.currently_on_leave, icon: BeachAccess, color: "#0369a1", bg: "#e0f2fe", sub: "Active today", highlight: false },
    { key: "withdrawn", label: "Withdrawn", value: counters.withdrawn, icon: Close, color: "#64748b", bg: "#e2e8f0", sub: "Withdrawn", highlight: false },
    { key: "cancelled", label: "Cancelled", value: counters.cancelled, icon: Close, color: "#64748b", bg: "#e2e8f0", sub: "Cancelled", highlight: false }
  ];

  const tabs = [
    { key: "pending", label: "Pending", count: counters.pending },
    { key: "approved", label: "Approved", count: counters.approved },
    { key: "rejected", label: "Rejected", count: counters.rejected },
    { key: "withdrawn", label: "Withdrawn", count: counters.withdrawn },
    { key: "cancelled", label: "Cancelled", count: counters.cancelled },
    { key: "onleave", label: "On Leave", count: counters.currently_on_leave },
    { key: "all", label: "All Requests", count: counters.total_applied }
  ];

  const activeTab = onLeaveOnly ? "onleave" : filter;

  return (
    <Page title="Leave Requests | Local Vocal Business Group">
      <Container maxWidth="xl">
        {/* Page Header */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, justifyContent: "space-between", gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
              Leave Requests
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
              Review, approve, and track employee time-off and leave quotas across departments.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download fontSize="small" />}
              onClick={exportCsv}
              sx={{ textTransform: "none", borderColor: "#cbd5e1", color: "#334155", fontWeight: 600, borderRadius: "8px", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}
            >
              Export Report
            </Button>
          </Stack>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <Grid item xs={6} sm={4} md key={k.label}>
                <Card
                  elevation={0}
                  onClick={() => setFilterTab(k.key)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    border: activeTab === k.key ? "2px solid #0B374D" : (k.highlight ? "2px solid #fbbf24" : "1px solid #e2e8f0"),
                    borderRadius: "12px",
                    background: activeTab === k.key ? "linear-gradient(to bottom, rgba(11,55,77,0.06), #fff)" : (k.highlight ? "linear-gradient(to bottom, rgba(254,243,199,0.4), #fff)" : "#fff"),
                    transition: "all .2s",
                    "&:hover": { borderColor: "#0B374D", transform: "translateY(-1px)" }
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: k.highlight ? "#92400e" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {k.label}
                    </Typography>
                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: k.bg, color: k.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.2, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 24, fontWeight: 900, color: k.highlight ? "#92400e" : "#0f172a", letterSpacing: "-0.02em" }}>
                      {k.value ?? 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: k.highlight ? "#92400e" : "#64748b",
                        bgcolor: k.highlight ? "#fef3c7" : "transparent",
                        px: k.highlight ? 1 : 0,
                        py: k.highlight ? 0.3 : 0,
                        borderRadius: k.highlight ? "6px" : 0,
                        textTransform: "uppercase"
                      }}
                    >
                      {k.sub}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Filter Toolbar */}
        <Card elevation={0} sx={{ p: 1.5, mb: 2, border: "1px solid #e2e8f0", borderRadius: "12px", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { md: "center" }, justifyContent: "space-between", gap: 1.5, width: "100%" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { xs: "stretch", sm: "center" }, flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
              <TextField
                size="small"
                placeholder="Filter by member name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") load();
                }}
                sx={{ minWidth: { xs: "100%", sm: 200 }, width: { xs: "100%", sm: "auto" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "#94a3b8" }} />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 150 }, width: { xs: "100%", sm: "auto" } }}>
                <InputLabel>Leave Type</InputLabel>
                <Select value={leaveTypeFilter} label="Leave Type" onChange={(e) => setLeaveTypeFilter(e.target.value)}>
                  <MenuItem value={0}>All Leave Types</MenuItem>
                  {(leaveTypes || []).map((lt) => (
                    <MenuItem key={lt.leave_t_id} value={lt.leave_t_id}>
                      {lt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" variant="contained" onClick={load} sx={{ textTransform: "none", bgcolor: LVB, "&:hover": { bgcolor: LVB_HOVER }, borderRadius: "8px", fontWeight: 600 }}>
                Apply
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSearch("");
                  setLeaveTypeFilter(0);
                  load(filter, "", 0);
                }}
                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, borderColor: "#cbd5e1", color: "#334155" }}
              >
                Clear
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* Table */}
        {isLoading ? (
          <CustomLoader />
        ) : (
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,0.06)" }}>
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #e2e8f0", py: 1.6 } }}>
                    <TableCell padding="checkbox">
                      <input type="checkbox" style={{ accentColor: LVB }} />
                    </TableCell>
                    <TableCell>Member</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell align="center">Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" sx={{ pr: 2.5 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(displayedRows || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                        No leave requests found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (displayedRows || []).map((r) => {
                      const st = statusStyle[r.status] || statusStyle[0];
                      const LIcon = leaveIcon(r.leave_type_name);
                      const [bg, color] = leaveColor(r.leave_type);
                      const pending = r.status === 0 || r.status === "0";
                      return (
                        <TableRow
                          key={r.leave_id}
                          hover
                          sx={{
                            bgcolor: pending ? "rgba(254,243,199,0.25)" : "transparent",
                            "&:hover": { bgcolor: pending ? "rgba(254,243,199,0.4)" : "#f8fafc" },
                            "& td": { borderBottom: "1px solid #f1f5f9", fontSize: 12, py: 1.3 }
                          }}
                        >
                          <TableCell padding="checkbox">
                            <input type="checkbox" style={{ accentColor: LVB }} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor: avatarColor(r.member_name),
                                  border: "1px solid rgba(11,55,77,0.12)"
                                }}
                              >
                                {initials(r.member_name)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
                                  {r.member_name}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                                  {r.business_name || r.mobile_no || ""}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={<LIcon sx={{ fontSize: "12px !important", color: `${color} !important` }} />}
                              label={r.leave_type_name}
                              sx={{
                                bgcolor: bg,
                                color: color,
                                fontWeight: 600,
                                fontSize: 10.5,
                                border: `1px solid ${color}22`,
                                height: 22,
                                "& .MuiChip-icon": { ml: 0.5 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>{r.start_date}</TableCell>
                          <TableCell sx={{ color: "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>{r.end_date}</TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 13, lineHeight: 1 }}>{r.total_days}</Typography>
                            <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 500 }}>
                              {r.total_days === 1 ? "day" : "days"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={st.label}
                              sx={{
                                bgcolor: st.bg,
                                color: st.color,
                                fontWeight: 700,
                                fontSize: 9.5,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                height: 20,
                                "&::before": pending
                                  ? { content: '""', width: 6, height: 6, borderRadius: "50%", bgcolor: st.dot, mr: 0.6, display: "inline-block", animation: "pulse 1.6s infinite" }
                                  : {}
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 2 }}>
                            {r.can_act ? (
                              <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  onClick={() => openAct(r, 2)}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#065f46",
                                    bgcolor: "#ecfdf5",
                                    border: "1px solid #a7f3d0",
                                    borderRadius: "6px",
                                    px: 1.2,
                                    py: 0.4,
                                    "&:hover": { bgcolor: "#059669", color: "#fff", borderColor: "#059669" }
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => openAct(r, 1)}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#be123c",
                                    bgcolor: "#fff1f2",
                                    border: "1px solid #fecdd3",
                                    borderRadius: "6px",
                                    px: 1.2,
                                    py: 0.4,
                                    "&:hover": { bgcolor: "#e11d48", color: "#fff", borderColor: "#e11d48" }
                                  }}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            ) : (
                              <Button
                                size="small"
                                startIcon={<Visibility sx={{ fontSize: 13 }} />}
                                onClick={() => openView(r)}
                                sx={{
                                  textTransform: "none",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: r.status === 1 ? "#be123c" : "#64748b",
                                  borderRadius: "6px",
                                  px: 1,
                                  "&:hover": { bgcolor: "#f1f5f9", color: LVB }
                                }}
                              >
                                {r.status === 1 ? "Reason" : "View Details"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer */}
            <Box sx={{ px: 2.5, py: 1.6, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", justifyContent: "space-between", gap: 1.5, fontSize: 12, color: "#475569" }}>
              <Typography sx={{ fontSize: 12, color: "#475569" }}>
                Showing <Box component="strong" sx={{ color: "#0f172a", fontWeight: 600 }}>{displayedRows.length ? 1 : 0}</Box> to{" "}
                <Box component="strong" sx={{ color: "#0f172a", fontWeight: 600 }}>{displayedRows.length}</Box> of{" "}
                <Box component="strong" sx={{ color: "#0f172a", fontWeight: 600 }}>{counters.total_applied ?? displayedRows.length}</Box> requests
              </Typography>
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>Rows per page:</Typography>
                  <Select size="small" value={10} sx={{ height: 26, fontSize: 11 }} disabled>
                    <MenuItem value={10}>10</MenuItem>
                  </Select>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#334155" }}>1-1 of 1</Typography>
                  <IconButton size="small" disabled sx={{ color: "#cbd5e1" }}><KeyboardArrowLeft fontSize="small" /></IconButton>
                  <IconButton size="small" sx={{ color: "#475569", "&:hover": { color: LVB } }}><KeyboardArrowRight fontSize="small" /></IconButton>
                </Stack>
              </Stack>
            </Box>
          </Card>
        )}

        {/* Approve/Reject Modal */}
        <Modal open={actModal} onClose={() => setActModal(false)}>
          <Box sx={modalStyle}>
            <Typography variant="h6" sx={{ mb: 1, textAlign: "center", fontWeight: 700, color: "#0f172a" }}>
              {actType === 2 ? "Approve Leave" : "Reject Leave"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", textAlign: "center", mb: 2 }}>
              {selected?.member_name} • {selected?.leave_type_name} • {selected?.start_date} → {selected?.end_date}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Remarks (required)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => setActModal(false)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={submitAct}
                disabled={isLoading}
                sx={{ textTransform: "none", borderRadius: "8px", bgcolor: actType === 2 ? "#059669" : "#e11d48", "&:hover": { bgcolor: actType === 2 ? "#047857" : "#be123c" } }}
              >
                {isLoading ? <CircularProgress size={16} color="inherit" /> : actType === 2 ? "Approve" : "Reject"}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* View Details Modal */}
        <Modal open={viewModal} onClose={() => setViewModal(false)}>
          <Box sx={modalStyle}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>Leave Details</Typography>
              <IconButton size="small" onClick={() => setViewModal(false)}><Close fontSize="small" /></IconButton>
            </Box>
            {detailLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
            ) : (
              <Stack spacing={1.4}>
                {[
                  ["Member", detail?.member_name],
                  ["Leave Type", detail?.leave_type_name],
                  ["From", detail?.start_date],
                  ["To", detail?.end_date],
                  ["Total Days", detail?.total_days],
                  ["Status", (statusStyle[detail?.status] || {}).label || detail?.status],
                  ["Applied On", detail?.applied_on],
                  ["Business", detail?.business_name],
                  ["Mobile", detail?.mobile_no],
                  ["Reason", detail?.reason]
                ].map(([label, val]) => (
                  <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, borderBottom: "1px dashed #e2e8f0", pb: 0.8 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", minWidth: 100 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12, color: "#0f172a", textAlign: "right", wordBreak: "break-word" }}>{val || "-"}</Typography>
                  </Box>
                ))}
                {detail?.action_remarks ? (
                  <Box sx={{ bgcolor: "#f8fafc", borderRadius: "8px", p: 1.2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 0.4 }}>Action Remarks</Typography>
                    <Typography sx={{ fontSize: 12, color: "#334155" }}>{detail.action_remarks}</Typography>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.4 }}>
                      by {detail.action_by_name || "-"} on {detail.action_on || "-"}
                    </Typography>
                  </Box>
                ) : null}
                {Array.isArray(detail?.attachments) && detail.attachments.length > 0 ? (
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 0.6 }}>Attachments</Typography>
                    <Stack spacing={0.5}>
                      {detail.attachments.map((a) => (
                        <Button
                          key={a.attachment_id}
                          size="small"
                          component="a"
                          href={a.file_url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ textTransform: "none", justifyContent: "flex-start", fontSize: 11, color: LVB }}
                        >
                          {a.file_name}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                ) : null}
              </Stack>
            )}
          </Box>
        </Modal>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </Container>
    </Page>
  );
}
